import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import isFunction from 'lodash/isFunction'
import {abbreviate, capitalCamelCase} from './utils'
import {defaults} from '../constants/products'
import {speedDialActivityNames} from '../constants/activities'

const paramToValue = (param, context) => {
  const sport = context.store.Sport ? context.store.Sport : defaults.SPORT
  const defaultContext = {
    classYear: defaults.CLASS_YEAR,
    firstName: defaults.FIRST_NAME,
    playerName: 'Player Name',
    playerNumber: defaults.PLAYER_NUMBER,
    sport
  }

  const {
    activity,
    classYear,
    design,
    firstName,
    playerName,
    playerNumber,
    store
  } = {
    ...defaultContext,
    ...omitBy(context, isNil)
  }

  const mascotImage = store.MascotImage ? store.MascotImage : store.Mascot
  const nextYear = (parseInt(classYear, 10) + 1).toString()
  const regExPublicSchoolWithNoName = new RegExp(/(PS.*d+)/g, 'i')
  const regExStreetNoName = new RegExp(/(\d+).+(?:STREET|AVENUE|ROAD)/g, 'i')
  const estYear = store.YearFrom || ''
  let printName = ''
  let schoolNameAbbreviation = ''
  let fullAbbreviation = ''
  let shortAbbreviation = ''
  let teamNameAbbreviation = ''
  let teamNameShortAbbreviation = ''

  const getShortAbbreviation = () => {
    if (schoolNameAbbreviation) {
      return schoolNameAbbreviation
    }

    if (store.ShortAbbreviation) {
      return store.ShortAbbreviation
    }
    let abbreviation = ''
    let cleansedSchoolName = cleanSchoolName(store.SchoolName)
      .replace('&', '')
      .replace('  ', ' ')
      .replace('  ', ' ')
    const streetNoNameMatch = regExStreetNoName.exec(cleansedSchoolName)
    const publicSchoolWithNoNameMatch =
      regExPublicSchoolWithNoName.exec(cleansedSchoolName)

    if (streetNoNameMatch) {
      abbreviation = streetNoNameMatch[1]
      schoolNameAbbreviation = abbreviation
      return abbreviation
    }
    if (publicSchoolWithNoNameMatch) {
      abbreviation = publicSchoolWithNoNameMatch[1]
      schoolNameAbbreviation = abbreviation
      return abbreviation
    }
    schoolNameAbbreviation = abbreviate(cleansedSchoolName)

    return schoolNameAbbreviation
  }

  const getLongAbbreviation = () => {
    const schoolName = store.SchoolName.toUpperCase()
    if (store.LongAbbreviation) {
      return store.LongAbbreviation
    }
    let abbreviation = getShortAbbreviation()
    if (schoolName.indexOf('HIGH') > 0 && schoolName.indexOf('JUNIOR') > 0) {
      abbreviation = `${abbreviation}JHS`
    } else if (schoolName.indexOf('HIGH') > 0) {
      abbreviation = `${abbreviation}HS`
    }
    return abbreviation
  }

  const setTeamNameAbbreviations = () => {
    teamNameAbbreviation = store.TeamNameAbbreviation
    teamNameShortAbbreviation = store.TeamNameShortAbbreviation

    if (!teamNameAbbreviation && store.TeamNameForPrint) {
      teamNameAbbreviation = abbreviate(store.TeamNameForPrint)
    }

    if (!teamNameShortAbbreviation) {
      if (store.JerseyName) {
        teamNameShortAbbreviation = abbreviate(store.JerseyName[0])
      } else if (store.Mascot) {
        teamNameShortAbbreviation = abbreviate(store.Mascot[0])
      }
    }
  }

  fullAbbreviation = getLongAbbreviation()

  if (store.StoreCode !== 't') {
    if (!store.SchoolNameForPrint) {
      printName = cleanSchoolName(store.SchoolName)
    } else {
      printName = store.SchoolNameForPrint
    }

    shortAbbreviation = store.ShortAbbreviation
      ? store.ShortAbbreviation
      : getShortAbbreviation()
  } else {
    printName = store.TeamNameForPrint ? store.TeamNameForPrint : store.TeamName
    setTeamNameAbbreviations()
  }

  const mascot = store.Mascot || ''
  const ladyMascot = store.LadyMascot || ''
  const shortMascotAbbrev = abbreviate(store.MascotAbbreviation || mascot)
  let selectedActivity

  if (
    activity &&
    (activity === mascot || activity.toLowerCase() === 'mascot')
  ) {
    selectedActivity = mascot
  } else {
    selectedActivity = speedDialActivityNames[activity] || activity
  }

  // Finally, if selectedActivity hasn't been set by logic above we default it to mascot.
  if (!selectedActivity) {
    selectedActivity = mascot
  }

  const selectedActivityTag = activity

  const teamNameForPrint = store.TeamNameForPrint
    ? store.TeamNameForPrint
    : store.TeamName

  const mapping = {
    // Military
    BASE: store.StoreCode !== 't' ? store.SchoolNameForPrint : teamNameForPrint,
    BRANCH: store.CatalogKey
      ? store.CatalogKey.replace('_', ' ').toUpperCase()
      : null,
    'SHORT BASE': null, // BaseShortName
    'SHORT UNIT': store.CatalogKey
      ? store.CatalogKey.replace('_', ' ').toUpperCase()
      : null,
    UNIT: mascot,

    // Greek
    'GREEK NAME 1': `${store.TeamName1} ${store.TeamName2}`,
    'GREEK NAME 2': `${store.TeamName1} ${store.TeamName2}`,
    'GREEK SHORT NAME': mascot,
    'GREEK SHORT NAME 2': ladyMascot,

    BLANK: '', // Not used
    'CUSTOM DATA': '', // Not used by any active products
    P1: () => replaceParameters(design.Parameter1, context),
    P2: () => replaceParameters(design.Parameter2, context),
    P3: () => replaceParameters(design.Parameter3, context),
    P4: () => replaceParameters(design.Parameter4, context),
    P5: () => replaceParameters(design.Parameter5, context),
    P6: () => replaceParameters(design.Parameter6, context),

    CITY: store.City,
    'COLOR 1': store.Color1,
    'COLOR 2': store.Color2,
    'COLOR 3': store.Color3,
    COUNTRY: store.Country,
    'END YEAR': store.YearTo,
    'EST YEAR': estYear,
    'EST YEAR12': estYear.slice(0, 2),
    'EST YEAR34': estYear.slice(2),
    'FIRST NAME': firstName,
    'FULL ABBREVIATION': fullAbbreviation,
    FULL_ABBREVIATION: fullAbbreviation,
    'JERSEY NAME': store.JerseyName || mascot,
    'LADY MASCOT': ladyMascot,
    'MASCOT IMAGE': mascotImage,
    'MASCOT NAME': mascot,
    NEXTYEAR12: nextYear.slice(0, 2),
    NEXTYEAR34: nextYear.slice(2),
    NEXTYEAR: nextYear,
    'PLAYER NUMBER': playerNumber,
    PLAYER: playerName,
    'PRINT NAME': printName,
    'SCHOOL FULL NAME': store.SchoolName,
    'SCHOOL FULL NAMEE': store.SchoolName,
    'SCHOOL ID': store.StoreID,
    SCHOOL: store.SchoolName,
    'SELECTED ACTIVITY TAG': selectedActivityTag,
    'SELECTED ACTIVITY': selectedActivity,
    'SHORT ABBREVIATION': shortAbbreviation,
    'SHORT MASCOT ABBREVIATION': shortMascotAbbrev,
    SPORT: sport,
    STADIUM: store.Stadium,
    STATE: store.State,
    'STATE ABBREVIATION': getStateAbbreviation(store.State),
    'STORE ID': store.StoreID,
    'TEAM FULL NAME': store.TeamNameForPrint || store.TeamName,
    'TEAM NAME ABBREVIATION': teamNameAbbreviation,
    'TEAM NAME SHORT ABBREVIATION': teamNameShortAbbreviation,
    YEAR12: classYear.slice(0, 2),
    YEAR34: classYear.slice(2),
    YEAR: classYear
  }
  const value =
    mapping[param] ||
    // In general, we can map 'FOO BAR' -> `School.FooBar`
    substituteTeamOrSchoolName(param, store) ||
    ''

  if (
    value &&
    value !== '' &&
    typeof value === 'string' &&
    value.toUpperCase() === 'LADY MASCOT'
  ) {
    return mapping[value.toUpperCase()]
  }
  if (isFunction(value)) {
    return value()
  }
  return value
}

// Parameter 'TEAM NAME' shouldn't be used over 'team name for print
const substituteTeamOrSchoolName = (param, store) => {
  if (param === 'TEAM NAME') {
    return store.TeamNameForPrint ? store.TeamNameForPrint : store.TeamName
  }
  return store[capitalCamelCase(param)]
}

const ghostWords = [
  'MIDDLE AND HIGH SCHOOL',
  'JUNIOR SENIOR HIGH SCHOOL',
  'SENIOR HIGH SCHOOL',
  'MIDDLE HIGH SCHOOL',
  'JUNIOR HIGH SCHOOL',
  'JUNIOR SENIOR SCHOOL',
  'ACADEMY',
  'EDUCATION CENTER',
  'KINDERGARTEN',
  'OPPORTUNITY',
  'PRIVATE',
  'PRIMARY',
  'TECHNICAL',
  'ADVENTIST',
  'COUNTY LINE',
  'EARLY CHILDHOOD',
  'AREA',
  'CENTER',
  'FOR THE PERFORMING ARTS',
  'ALTERNATIVE',
  'COLLEGE',
  'INTERNATIONAL',
  'ACHIEVEMENT',
  //"CONSOLIDATED",
  'COMMUNITY',
  'FOR THE',
  'MAGNET',
  'EDUCATION CENTER',
  'ELEMENTARY SCHOOL',
  'SOUTH CAMPUS',
  'NORTH CAMPUS',
  'EAST CAMPUS',
  'WEST CAMPUS',
  'CONSOLIDATED',
  'COMMUNITY',
  'HIGH SCHOOL',
  'MIDDLE SCHOOL',
  'SECONDARY SCHOOL',
  'ELEMENTARY SCHOOL',
  'HIGH SCH',
  'SENIOR HIGH',
  'INTERMEDIATE',
  ' HS ',
  'H S ',
  ' H S',
  ' SCHOOLS',
  ' SCHOOL'
]

const ghostWords2 = [
  'MIDDLE AND HIGH SCHOOL',
  'JUNIOR SENIOR HIGH SCHOOL',
  'SENIOR HIGH SCHOOL',
  'MIDDLE HIGH SCHOOL',
  'JUNIOR HIGH SCHOOL',
  'JUNIOR SENIOR SCHOOL',
  'ACADEMY',
  'EDUCATION CENTER',
  'KINDERGARTEN',
  'OPPORTUNITY',
  'PRIVATE',
  'PRIMARY',
  'EDUCATION CENTER',
  'ELEMENTARY SCHOOL',
  'HIGH SCHOOL',
  'MIDDLE SCHOOL',
  'SECONDARY SCHOOL',
  'ELEMENTARY SCHOOL',
  'HIGH SCH',
  'SENIOR HIGH',
  ' HS ',
  'H S ',
  ' H S',
  ' SCHOOLS',
  ' SCHOOL'
]

const findMatchesAndRemove = (schoolName, ghostWords) => {
  let schoolNameCleansed = ''
  if (schoolName && ghostWords.length > 0) {
    schoolNameCleansed = schoolName.toUpperCase()
    for (let i = 0; i < ghostWords.length; i++) {
      if (!schoolNameCleansed.startsWith(ghostWords[i])) {
        schoolNameCleansed = schoolNameCleansed.replace(ghostWords[i], '')
      }
    }
    return schoolNameCleansed
  }
}
const cleanSchoolName = (schoolName) => {
  if (!schoolName) return ''
  let schoolNameCleaned = findMatchesAndRemove(schoolName, ghostWords)
  if (schoolNameCleaned.trim().length === 0) {
    schoolNameCleaned = findMatchesAndRemove(schoolName, ghostWords2)
  }
  if (schoolNameCleaned.endsWith('HIGH')) {
    schoolNameCleaned = schoolNameCleaned.replace('HIGH', '')
  }
  schoolNameCleaned = schoolNameCleaned.replace(/\w+/g, function (w) {
    return w[0].toUpperCase() + w.slice(1).toLowerCase()
  })

  return schoolNameCleaned
}
//The state model wasn't implemented on the store API correctly.
//When the state model is added it breaks the store page. For now, we are using
// the static state data since the data is not likely to change. If we support more countries we will need to
// reevaluate this approach.
const getStateAbbreviation = (state) => {
  const stateList = {
    Arizona: 'AZ',
    Alabama: 'AL',
    Alaska: 'AK',
    Arkansas: 'AR',
    'American Samoa': 'AS',
    California: 'CA',
    Colorado: 'CO',
    Connecticut: 'CT',
    'Washington D.C.': 'DC',
    Delaware: 'DE',
    Florida: 'FL',
    Georgia: 'GA',
    Hawaii: 'HI',
    Idaho: 'ID',
    Illinois: 'IL',
    Indiana: 'IN',
    Iowa: 'IA',
    Kansas: 'KS',
    Kentucky: 'KY',
    Louisiana: 'LA',
    Maine: 'ME',
    Maryland: 'MD',
    Massachusetts: 'MA',
    Michigan: 'MI',
    Minnesota: 'MN',
    Mississippi: 'MS',
    Missouri: 'MO',
    Montana: 'MT',
    Nebraska: 'NE',
    Nevada: 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    Ohio: 'OH',
    Oklahoma: 'OK',
    Oregon: 'OR',
    Pennsylvania: 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    Tennessee: 'TN',
    Texas: 'TX',
    Utah: 'UT',
    Vermont: 'VT',
    Virginia: 'VA',
    Washington: 'WA',
    'West Virginia': 'WV',
    Wisconsin: 'WI',
    Wyoming: 'WY',
    Alberta: 'AB',
    'British Columbia': 'BC',
    Manitoba: 'MB',
    'New Brunswick': 'NB',
    'Newfoundland and Labrador': 'NL',
    'Northwest Territories': 'NT',
    'Nova Scotia': 'NS',
    Nunavut: 'NU',
    Ontario: 'ON',
    Quebec: 'QC',
    Saskatchewan: 'SK',
    Yukon: 'YT'
  }
  if (!state) {
    return ''
  }
  return stateList[state]
}

// Note: these implementations are lifted from the PrepWeb project. Be wary of
// changing them.
const modifierToFn = {
  '->UC': (a) => a.toUpperCase(),
  '->CC': (a) => capitalCamelCase(a, ' '),
  '->FC': (a) => `${a[0].toUpperCase()}${a.slice(1).toLowerCase()}`,
  '->FL': (a) =>
    `${a[0].toUpperCase()}${a.slice(1, a.length - 1).toLowerCase()}${a[
      a.length - 1
    ].toUpperCase()}`,
  '->LC': (a) => a.toLowerCase()
}

const handleTradeMark = (text, context) => {
  const base = text.slice(0, -4)
  const tradeMarks = context.store.TradeMarksList
  let trademark = ''
  if (tradeMarks) {
    const tradeMarkList = tradeMarks.split(',')

    if (tradeMarkList === null) return trademark
    trademark = handleMultipleTrademarks(base, tradeMarkList)
    if (tradeMarkList.length > 0 && trademark === '') {
      for (let i = 0; i < tradeMarkList.length; i++) {
        const tradeMark = tradeMarkList[i]
        const compareAgainst = tradeMark.substring(0, tradeMark.length - 1)

        //%variables%->TM
        if (compareAgainst.length >= 1) {
          if (formatString(base) === formatString(compareAgainst)) {
            trademark = tradeMark.slice(-1)
          }
        }
        //->TM
        if (!base) {
          if (tradeMarkList.length === 1) {
            trademark = tradeMark
          }
        }
      }
    }
  }
  return trademark
}

function handleMultipleTrademarks(base, tradeMarkList) {
  let tm = ''
  if (base && tradeMarkList.length > 0) {
    for (let i = 0; i < tradeMarkList.length; i++) {
      const tradeMark = tradeMarkList[i]
      const tradeMarkWithoutLastChar = tradeMark.slice(0, -1)
      if (base === tradeMarkWithoutLastChar) {
        tm = tradeMark.slice(-1)
      }
    }
  }
  return tm
}

function formatString(string) {
  let formatted
  if (string) {
    formatted = string.trim().toUpperCase()
  }
  return formatted
}

export const replaceParameters = (str, context) => {
  if (!str) {
    return ''
  }

  // Replace interpolated params
  let text = str.replace(/%([^%]+)%/g, (match, param) => {
    return paramToValue(param, context)
  })

  // Apply and replace modifier
  let trademark
  let modifier

  text = text.replace(/(->\w+)/, (match, mod) => {
    if (modifierToFn[mod]) {
      modifier = modifierToFn[mod]
    }
    if (mod === '->TM') {
      trademark = handleTradeMark(text, context)
    }
    return ''
  })

  // Handle boolean logic. For now "OR" operands, but this may be incomplete.
  if (/\|/.test(text)) {
    text = text.replace(/([^\|]+)\|([^->]+)/, (match, a, b) => {
      return a || b
    })
  }

  if (modifier) {
    text = modifier(text)
    text = text.replace(/(->\w+)/, '')
  }

  if (trademark || trademark === '') {
    text = trademark
  }

  return text
}

export const replaceSelector = (design, context) => {
  if (!design.Selector) {
    return design.Selector
  }

  let text = replaceParameters(design.Selector, context)
  text = text.replace('*', `-${context.productColor}`)

  return text
}
